import React, { useState, useEffect } from 'react';
import { Plus, FileText, X } from 'lucide-react';
import { useOrders } from '../hooks/useOrders';
import { useAuth } from '../contexts/AuthContext';
import { deleteOrder } from '../utils/storage';
import OrdersTable from '../components/client/OrdersTable';
import OrderDetails from '../components/common/OrderDetails';
import PersistenceIndicator from '../components/common/PersistenceIndicator';
import OrderForm from '../components/client/OrderForm';

const ClientDashboard = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('tasks');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const { orders, loading: isLoading, error, unreadCounts, handleSendMessage, handleMarkMessagesAsRead, handleWebSocketMessage, handleUpdateOrder, refetch } = useOrders();
    const [patientFilter, setPatientFilter] = useState({
        patientNameCode: ''
    });

    const [persistenceMessage, setPersistenceMessage] = useState('');
    const [persistenceType, setPersistenceType] = useState('success');

    // Effect pour gerer les parametres d'URL
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const patientNameCode = urlParams.get('patientNameCode');

        if (patientNameCode) {
            setPatientFilter({
                patientNameCode
            });
        }
    }, []);

    const handleOrderClick = (order) => {
        setSelectedOrder(order);
    };

    const handleCloseOrderDetails = () => {
        setSelectedOrder(null);
    };

    // Synchroniser selectedOrder avec orders quand orders change
    // (ex: après envoi d'un message, les messages sont mis à jour dans orders)
    useEffect(() => {
        if (selectedOrder) {
            const updatedOrder = orders.find(o => o.id === selectedOrder.id);
            if (updatedOrder && updatedOrder !== selectedOrder) {
                setSelectedOrder(updatedOrder);
            }
        }
    }, [orders, selectedOrder]);

    const handlePatientClick = (patientInfo) => {
        const { patientNameCode } = patientInfo;

        if (selectedOrder) {
            setSelectedOrder(null);
        }

        setPatientFilter({
            patientNameCode
        });

        const urlParams = new URLSearchParams();
        urlParams.set('patientNameCode', patientNameCode);

        const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
        window.history.pushState({}, '', newUrl);
    };

    const clearFilters = () => {
        setPatientFilter({
            patientNameCode: ''
        });

        window.history.pushState({}, '', window.location.pathname);
    };

    const filteredOrders = patientFilter.patientNameCode
        ? orders.filter(order =>
            order.patient_name_code?.toUpperCase() === patientFilter.patientNameCode.toUpperCase()
          )
        : orders;

    const handleDeleteOrder = async (orderId) => {
        try {
            await deleteOrder(orderId);
            await refetch();
            setPersistenceMessage('Commande annulée avec succès');
            setPersistenceType('success');
        } catch (error) {
            console.error('Erreur lors de l\'annulation de la commande:', error);
            setPersistenceMessage('Erreur lors de l\'annulation de la commande');
            setPersistenceType('error');
        }

        setTimeout(() => {
            setPersistenceMessage('');
        }, 5000);
    };

    const handleOrderFormSuccess = async () => {
        await refetch();
        setPersistenceMessage('Commande creee avec succes !');
        setPersistenceType('success');
        setActiveTab('tasks');

        setTimeout(() => {
            setPersistenceMessage('');
        }, 5000);
    };

    return (
        <div className="min-h-screen py-6">
            <div className="mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header moderne */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-white mb-2">
                                Tableau de bord
                            </h1>
                            <p className="text-gray-400 text-lg">
                                Gerez vos commandes de protheses dentaires
                            </p>
                        </div>
                    </div>
                </div>

                {/* Indicateur de persistance */}
                <PersistenceIndicator
                    isVisible={!!persistenceMessage}
                    message={persistenceMessage}
                    type={persistenceType}
                />

                {/* Vue principale avec corbeille des taches */}
                <div className="mb-8">
                    {activeTab === 'tasks' && (
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-white">
                                Corbeille des taches
                            </h2>
                            <button
                                onClick={() => setActiveTab('new-order')}
                                className="bg-yellow-500 hover:bg-yellow-400 text-black px-6 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center space-x-2"
                            >
                                <Plus className="w-5 h-5" />
                                <span>Nouvelle commande</span>
                            </button>
                        </div>
                    )}

                    {activeTab === 'new-order' && (
                        <div className="flex items-center mb-6">
                            <button
                                onClick={() => setActiveTab('tasks')}
                                className="mr-4 p-2 text-gray-400 hover:text-gray-200 hover:bg-white/10 rounded-lg transition-colors"
                                title="Retour a la corbeille"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <h2 className="text-2xl font-bold text-white">
                                Nouvelle commande
                            </h2>
                        </div>
                    )}
                </div>

                {/* Contenu des vues */}
                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
                        <span className="ml-3 text-gray-400">Chargement des donnees...</span>
                    </div>
                ) : activeTab === 'tasks' ? (
                    <>
                        {/* Indicateur de filtrage */}
                        {patientFilter.patientNameCode && (
                            <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <FileText className="h-5 w-5 text-yellow-400" />
                                        <div>
                                            <p className="text-sm font-medium text-yellow-300">
                                                Historique filtre pour le patient
                                            </p>
                                            <p className="text-lg font-semibold text-yellow-400">
                                                {patientFilter.patientNameCode}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={clearFilters}
                                        className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-colors"
                                        title="Effacer le filtre"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                                <p className="text-sm text-yellow-400 mt-2">
                                    {filteredOrders.length} commande{filteredOrders.length > 1 ? 's' : ''} trouvee{filteredOrders.length > 1 ? 's' : ''}
                                </p>
                            </div>
                        )}

                        <div className="glass border border-white/10 rounded-2xl shadow-lg overflow-hidden">
                            <div className="bg-gradient-to-r from-yellow-500/15 to-transparent border-b border-white/[0.07] px-6 py-4">
                                <h3 className="text-xl font-semibold text-white">
                                    Mes commandes en cours
                                </h3>
                                <p className="text-gray-300 text-sm mt-1">
                                    Suivez l'etat de vos commandes et leur progression
                                </p>
                            </div>
                            <div className="p-6">
                                <OrdersTable
                                    orders={filteredOrders}
                                    onOrderClick={handleOrderClick}
                                    onDeleteOrder={handleDeleteOrder}
                                    onPatientClick={handlePatientClick}
                                    unreadCounts={unreadCounts}
                                />
                            </div>
                        </div>
                    </>
                ) : activeTab === 'new-order' ? (
                    <div className="glass border border-white/10 rounded-2xl shadow-lg overflow-hidden">
                        <div className="bg-gradient-to-r from-yellow-500/15 to-transparent border-b border-white/[0.07] px-6 py-4">
                            <h2 className="text-xl font-semibold text-white">
                                Nouvelle commande
                            </h2>
                            <p className="text-gray-300 text-sm mt-1">
                                Remplissez les informations pour creer une nouvelle commande
                            </p>
                        </div>

                        <OrderForm
                            onSubmitSuccess={handleOrderFormSuccess}
                            onCancel={() => setActiveTab('tasks')}
                        />
                    </div>
                ) : null}

                {/* Modal de details de commande */}
                {selectedOrder && (
                    <OrderDetails
                        order={selectedOrder}
                        onClose={handleCloseOrderDetails}
                        onDeleteOrder={handleDeleteOrder}
                        onSendMessage={handleSendMessage}
                        onMarkMessagesAsRead={handleMarkMessagesAsRead}
                        onWebSocketMessage={handleWebSocketMessage}
                        onOrderUpdated={handleUpdateOrder}
                        currentUserType="client"
                        currentUserName={user?.name || 'Client'}
                        currentUserId={user?.id}
                        onPatientClick={handlePatientClick}
                    />
                )}
            </div>
        </div>
    );
};

export default ClientDashboard;
